import { createLogic } from 'redux-logic';
import { flatten, get, map, mapKeys, set, size } from 'lodash';
import { normalize, schema } from 'normalizr';

export const initialState = {
  entities: {},
  pages: {},
  pagination: {},
  stats: {},
  hasReviewed: false,
  isLoading: false,
};

//
// Actions

const FETCH = '@@app/reviews/FETCH';
const FETCH_SUCCESS = '@@app/reviews/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/reviews/FETCH_ERROR';

const FETCH_REPLIES = '@@app/reviews/FETCH_REPLIES';
const FETCH_REPLIES_SUCCESS = '@@app/reviews/FETCH_REPLIES_SUCCESS';
const FETCH_REPLIES_ERROR = '@@app/reviews/FETCH_REPLIES_ERROR';

const REVIEW = '@@app/reviews/REVIEW';
const REVIEW_SUCCESS = '@@app/reviews/REVIEW_SUCCESS';
const REVIEW_ERROR = '@@app/reviews/REVIEW_ERROR';

const LIKE = '@@app/reviews/LIKE';
const LIKE_SUCCESS = '@@app/reviews/LIKE_SUCCESS';
const LIKE_ERROR = '@@app/reviews/LIKE_ERROR';

const STATS = '@@app/reviews/STATS';
const STATS_SUCCESS = '@@app/reviews/STATS_SUCCESS';
const STATS_ERROR = '@@app/reviews/STATS_ERROR';

const scopeKey = (type, id) => {
  return `${type}_${id}`;
};

const scope = (obj, type, id) => {
  return { [scopeKey(type, id)]: obj };
};

const paginate = (items, pagination, type, id) => {
  const pages = { [pagination.page]: items };
  return scope(pages, type, id);
};

//
// Observables

const fetchReviewsLogic = createLogic({
  type: FETCH,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ action, httpClient }) {
    const {
      memberId,
      voomeProductId,
      reviewableId,
      reviewableType,
      page,
    } = action.payload;
    const params = {
      reviewableId,
      reviewableType,
      page,
    };

    return httpClient
      .get(`/v3/members/${memberId}/voome-products/${voomeProductId}/reviews`, {
        params,
      })
      .then(({ data, pagination }) => {
        const reviews = normalize(data, [new schema.Entity('reviews')]);
        const pages = paginate(
          reviews.result,
          pagination,
          reviewableType,
          reviewableId
        );

        return {
          reviews,
          pagination: scope(pagination, reviewableType, reviewableId),
          pages,
          reviewableType,
          reviewableId,
          voomeProductId,
        };
      });
  },
});

const repliesFetchLogic = createLogic({
  type: FETCH_REPLIES,
  processOptions: {
    successType: FETCH_REPLIES_SUCCESS,
    failType: FETCH_REPLIES_ERROR,
  },
  process({ action, httpClient }) {
    const {
      memberId,
      voomeProductId,
      reviewableId,
      reviewableType,
      parentId,
    } = action.payload;

    const params = {
      reviewableId,
      reviewableType,
      parentId,
    };

    return httpClient
      .get(`/v3/members/${memberId}/voome-products/${voomeProductId}/reviews`, {
        params,
      })
      .then(response => {
        const data = response.data;
        const replies = mapKeys(flatten(data), 'id');
        const keys = map(replies, ({ id }) => id);

        return { replies, keys, parentId };
      });
  },
});

const reviewLogic = createLogic({
  type: REVIEW,
  processOptions: {
    successType: REVIEW_SUCCESS,
    failType: REVIEW_ERROR,
  },
  process({ action, httpClient }) {
    const { memberId, voomeProductId, review } = action.payload;
    const endpoint = `/v3/members/${memberId}/voome-products/${voomeProductId}/reviews`;
    const params = { review };
    const onSuccess = response => response.data;
    return httpClient.post(endpoint, params).then(onSuccess);
  },
});

const reviewLikeLogic = createLogic({
  type: LIKE,
  processOptions: {
    successType: LIKE_SUCCESS,
    failType: LIKE_ERROR,
  },
  process({ action, httpClient, getState }) {
    const { memberId, data } = action.payload;
    const review = selectReview(getState().reviews, data.subjectId);
    const endpoint = `/v1/members/${memberId}/activities`;

    return httpClient.post(endpoint, data).then(() => {
      return {
        ...review,
        liked: true,
        likesCount: review.likesCount + 1,
      };
    });
  },
});

const reviewsStatsLogic = createLogic({
  type: STATS,
  processOptions: {
    successType: STATS_SUCCESS,
    failType: STATS_ERROR,
  },
  process({ action, httpClient }) {
    const {
      memberId,
      voomeProductId,
      reviewableId,
      reviewableType,
    } = action.payload;

    const params = {
      reviewableId,
      reviewableType,
    };

    const endpoint = `/v3/members/${memberId}/voome-products/${voomeProductId}/reviews/stats`;

    return httpClient.get(endpoint, { params }).then(response => {
      return { stats: response.data };
    });
  },
});

export const reviewsLogic = [
  fetchReviewsLogic,
  repliesFetchLogic,
  reviewLogic,
  reviewLikeLogic,
  reviewsStatsLogic,
];

//
// Selectors

export const selectReviews = state => {
  return state.entities.reviews;
};

export const selectReview = (state, id) => {
  const reviews = selectReviews(state);
  return reviews ? reviews[id] : undefined;
};

export const selectReplies = state => {
  return state.entities.replies;
};

export const selectReviewsPagination = (state, { id, type }) => {
  return state.pagination[scopeKey(type, id)];
};

export const selectReviewsPages = (state, { id, type }) => {
  return state.pages[scopeKey(type, id)];
};

export const selectReviewsStats = state => {
  return state.stats;
};

//
// Action Creators

export const fetchReviews = (
  memberId,
  voomeProductId,
  reviewableId,
  reviewableType,
  page = 1
) => {
  return {
    type: FETCH,
    payload: { memberId, voomeProductId, reviewableId, reviewableType, page },
  };
};

export const fetchReplies = (
  memberId,
  voomeProductId,
  reviewableId,
  reviewableType,
  parentId
) => {
  return {
    type: FETCH_REPLIES,
    payload: {
      memberId,
      voomeProductId,
      reviewableId,
      reviewableType,
      parentId,
    },
  };
};

export const createReview = (memberId, voomeProductId, params) => {
  const { reviewableId, reviewableType, review, rating, parentId } = params;

  return {
    type: REVIEW,
    payload: {
      memberId,
      voomeProductId,
      review: {
        parentId,
        rating,
        reviewableId,
        reviewableType,
        review,
      },
    },
  };
};

export const createProgramReview = (
  memberId,
  voomeProductId,
  review,
  rating
) => {
  return {
    type: REVIEW,
    payload: {
      memberId,
      voomeProductId,
      review: {
        reviewableId: voomeProductId,
        reviewableType: 'Voome::Product',
        review,
        rating,
      },
    },
  };
};

export const likeReview = (memberId, subjectId, associatedId) => {
  return {
    type: LIKE,
    payload: {
      memberId,
      data: {
        subjectType: 'Review',
        activity: 'like',
        associatedType: 'VoomeProgram',
        subjectId,
        associatedId,
      },
    },
  };
};

export const fetchReviewsStats = (
  memberId,
  voomeProductId,
  reviewableId,
  reviewableType
) => {
  return {
    type: STATS,
    payload: { memberId, voomeProductId, reviewableId, reviewableType },
  };
};

//
// Reducer

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  let entities;
  let key;

  switch (type) {
    case FETCH:
    case STATS:
      return {
        ...state,
        isLoading: true,
      };

    case FETCH_SUCCESS: {
      const { reviews, pagination } = payload;
      key = scopeKey(payload.reviewableType, payload.reviewableId);

      let { pages } = payload;
      pages = { ...get(state.pages, key), ...get(pages, key) };
      pages = scope(pages, payload.reviewableType, payload.reviewableId);

      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          reviews: {
            ...get(state, 'entities.reviews'),
            ...get(reviews, 'entities.reviews'),
          },
        },
        pages: { ...state.pages, ...pages },
        pagination: { ...state.pagination, ...pagination },
      };
    }

    case FETCH_ERROR:
    case STATS_ERROR:
      return {
        ...state,
        isLoading: false,
      };

    case FETCH_REPLIES_SUCCESS: {
      const { replies, keys, parentId } = payload;
      const reviews = { ...get(state, 'entities.reviews') };
      reviews[parentId].replies = keys;
      reviews[parentId].isLoading = false;

      return {
        ...state,
        entities: {
          ...state.entities,
          replies: {
            ...get(state, 'entities.replies', {}),
            ...payload.replies,
          },
        },
      };
    }

    case FETCH_REPLIES:
      const { parentId } = payload;
      const reviews = { ...get(state, 'entities.reviews') };
      reviews[parentId].isLoading = true;

      return { ...state };

    case REVIEW:
      return {
        ...state,
        hasReviewed: false,
      };

    case REVIEW_SUCCESS: {
      const pageState = { ...state };
      const entity = { ...payload, highlight: true };
      const reviews = { ...get(state, 'entities.reviews') };
      const replies = { ...get(state, 'entities.replies') };

      if (payload.parentId) {
        const parentReplies = reviews[payload.parentId].replies || [];
        reviews[payload.parentId].replies = [...parentReplies, payload.id];
        replies[payload.id] = entity;
      } else {
        // This payload is a review
        if (size(pageState.pagination) > 0) {
          const key = scopeKey(payload.reviewableType, payload.reviewableId);
          const page = pageState.pages[key] && pageState.pages[key][1];

          if (page) {
            page.unshift(payload.id);
          }
        }

        reviews[payload.id] = entity;
      }

      return {
        ...pageState,
        entities: {
          ...state.entities,
          reviews,
          replies,
        },
        hasReviewed: true,
      };
    }

    case LIKE_SUCCESS:
      const newState = { ...state };
      return set(newState, `entities.reviews.${payload.id}`, payload);

    case STATS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        stats: payload.stats,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/reviews.js